import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Clock, Users, CheckCircle, Play, BookOpen } from 'lucide-react';

function CourseDetailPage() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Mock course data - in a real app, this would come from an API
  const course = {
    id: id,
    title: "Complete Web Development Bootcamp",
    description: "Learn web development from scratch with HTML, CSS, JavaScript, React, Node.js, and more. Build real-world projects and become a full-stack developer.",
    category: "Programming",
    duration: "40 hours",
    students: 15420,
    rating: 4.8,
    price: "$89.99",
    level: "Beginner",
    instructor: "Sarah Johnson",
    instructorTitle: "Senior Full-Stack Developer",
    instructorCompany: "TechCorp Inc.",
    features: [
      "HTML & CSS Fundamentals",
      "JavaScript ES6+ Mastery",
      "React.js Development",
      "Node.js Backend",
      "MongoDB Database",
      "Real-World Projects",
      "Lifetime Access",
      "Certificate of Completion"
    ],
    curriculum: [
      {
        section: "Getting Started",
        lectures: [
          { title: "Course Introduction", duration: "5:30", type: "video" },
          { title: "Setting Up Your Development Environment", duration: "12:45", type: "video" },
          { title: "How to Get Help", duration: "3:20", type: "video" }
        ]
      },
      {
        section: "HTML & CSS Fundamentals",
        lectures: [
          { title: "HTML Structure and Elements", duration: "25:15", type: "video" },
          { title: "CSS Styling and Layout", duration: "32:40", type: "video" },
          { title: "Responsive Design Principles", duration: "28:30", type: "video" },
          { title: "CSS Flexbox and Grid", duration: "35:20", type: "video" }
        ]
      },
      {
        section: "JavaScript Programming",
        lectures: [
          { title: "JavaScript Basics", duration: "45:10", type: "video" },
          { title: "ES6+ Features", duration: "38:25", type: "video" },
          { title: "DOM Manipulation", duration: "42:15", type: "video" },
          { title: "Async Programming", duration: "50:30", type: "video" }
        ]
      }
    ],
    requirements: [
      "No prior programming experience required",
      "A computer with internet connection",
      "Willingness to learn and practice",
      "Basic computer skills"
    ],
    targetAudience: [
      "Complete beginners who want to learn web development",
      "Students looking to build a portfolio",
      "Professionals wanting to switch careers",
      "Anyone interested in creating websites and web applications"
    ]
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <Star key={`full-${i}`} className="w-5 h-5 fill-current text-yellow-500" />
      );
    }

    if (hasHalfStar) {
      stars.push(
        <Star key="half" className="w-5 h-5 fill-current text-yellow-500" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <Star key={`empty-${i}`} className="w-5 h-5 text-slate-300" />
      );
    }

    return stars;
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'curriculum', label: 'Curriculum' },
    { id: 'instructor', label: 'Instructor' },
    { id: 'reviews', label: 'Reviews' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        {/* Course Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-8 mb-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="mb-4">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {course.category}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {course.title}
              </h1>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {course.description}
              </p>
              
              {/* Course Stats */}
              <div className="flex items-center space-x-6 mb-6">
                <div className="flex items-center space-x-2">
                  {renderStars(course.rating)}
                  <span className="text-slate-600 font-medium">
                    {course.rating} ({course.students.toLocaleString()} students)
                  </span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <Clock className="w-5 h-5" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center space-x-2 text-slate-600">
                  <Users className="w-5 h-5" />
                  <span>{course.level}</span>
                </div>
              </div>

              {/* Course Features */}
              <div className="grid grid-cols-2 gap-3">
                {course.features.slice(0, 6).map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Course Card */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl p-6 border border-blue-200">
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-blue-600 mb-2">{course.price}</div>
                  <div className="text-slate-600">Lifetime Access</div>
                </div>
                
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-4 rounded-lg transition-all duration-300 mb-4 flex items-center justify-center space-x-2">
                  <Play className="w-5 h-5" />
                  <span>Enroll Now</span>
                </button>
                
                <button className="w-full bg-white hover:bg-slate-50 text-blue-600 font-semibold px-6 py-4 rounded-lg border-2 border-blue-600 transition-all duration-300 flex items-center justify-center space-x-2">
                  <BookOpen className="w-5 h-5" />
                  <span>Preview Course</span>
                </button>

                <div className="mt-6 text-center">
                  <div className="text-sm text-slate-600 mb-2">30-Day Money-Back Guarantee</div>
                  <div className="text-xs text-slate-500">Full access • Certificate • Support</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg mb-8"
        >
          <div className="border-b border-slate-200">
            <div className="flex space-x-8 px-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-6 px-2 border-b-2 font-medium transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-8">
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">What you'll learn</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {course.features.map((feature, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Requirements</h3>
                  <ul className="space-y-2">
                    {course.requirements.map((req, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-slate-700">{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">Who this course is for</h3>
                  <ul className="space-y-2">
                    {course.targetAudience.map((audience, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-slate-700">{audience}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'curriculum' && (
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Course Content</h3>
                <div className="space-y-4">
                  {course.curriculum.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="border border-slate-200 rounded-lg">
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                        <h4 className="font-semibold text-slate-900">{section.section}</h4>
                      </div>
                      <div className="divide-y divide-slate-200">
                        {section.lectures.map((lecture, lectureIndex) => (
                          <div key={lectureIndex} className="px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Play className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-700">{lecture.title}</span>
                            </div>
                            <span className="text-slate-500 text-sm">{lecture.duration}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'instructor' && (
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  {course.instructor.charAt(0)}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{course.instructor}</h3>
                <p className="text-blue-600 font-medium mb-1">{course.instructorTitle}</p>
                <p className="text-slate-600 mb-6">{course.instructorCompany}</p>
                <p className="text-slate-700 max-w-2xl mx-auto">
                  Sarah is a senior full-stack developer with over 8 years of experience in web development. 
                  She has worked with major tech companies and has helped thousands of students learn web development 
                  through her comprehensive courses.
                </p>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Student Reviews</h3>
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">⭐</div>
                  <h4 className="text-xl font-semibold text-slate-900 mb-2">No reviews yet</h4>
                  <p className="text-slate-600">Be the first to review this course!</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default CourseDetailPage;
